import { z } from 'zod'
import {
  ArrangementSchema,
  RevisionSchema,
  SCHEMA_VERSION,
  SongSchema,
  type Arrangement,
  type Revision,
  type Song,
} from './schema/v1'
import { PracticeSchema, type Practice } from './practice'
import { hasErrors, validateContent, type Issue } from './validate'

/**
 * Copia de seguridad completa (§3.4, §9.5): un JSON con todas las canciones, arreglos y
 * revisiones y su versión de esquema. Safari 15 no garantiza que conserve los datos, así que
 * esta copia es la red de seguridad hasta que llegue la sincronización (E4).
 */
/** 2: añade la memoria de práctica (E2). Las copias de la versión 1 se siguen leyendo. */
export const BACKUP_VERSION = 2 as const

export const BackupSchema = z.object({
  app: z.literal('atril'),
  backupVersion: z.number().int().positive(),
  schemaVersion: z.number().int().positive(),
  exportedAt: z.iso.datetime(),
  songs: z.array(SongSchema),
  arrangements: z.array(ArrangementSchema),
  revisions: z.array(RevisionSchema),
  /** Memoria de práctica de cada arreglo (desde la versión 2). */
  practice: z.array(PracticeSchema).optional(),
  /** Preferencias de la app (notación, tema, pedal…). Se validan al restaurarlas. */
  preferences: z.record(z.string(), z.unknown()).optional(),
})

export type Backup = z.infer<typeof BackupSchema>

export interface BackupData {
  songs: Song[]
  arrangements: Arrangement[]
  revisions: Revision[]
  practice?: Practice[]
  preferences?: Record<string, unknown>
}

export function createBackup(data: BackupData, now: string): Backup {
  const backup: Backup = {
    app: 'atril',
    backupVersion: BACKUP_VERSION,
    schemaVersion: SCHEMA_VERSION,
    exportedAt: now,
    songs: data.songs,
    arrangements: data.arrangements,
    revisions: data.revisions,
  }
  if (data.practice && data.practice.length > 0) backup.practice = data.practice
  if (data.preferences) backup.preferences = data.preferences
  return backup
}

export function serializeBackup(backup: Backup): string {
  return JSON.stringify(backup, null, 2) + '\n'
}

/** Nombre del archivo de la copia: la extensión doble está en `.gitignore`. */
export function backupFileName(now: string): string {
  return `atril-${now.slice(0, 10)}.atril-backup.json`
}

export interface BackupSummary {
  songs: number
  arrangements: number
  revisions: number
}

export function summarizeBackup(
  backup: Pick<Backup, 'songs' | 'arrangements' | 'revisions'>,
): BackupSummary {
  return {
    songs: backup.songs.length,
    arrangements: backup.arrangements.length,
    revisions: backup.revisions.length,
  }
}

/** Coherencia entre las piezas: lo que el esquema no puede comprobar mirando cada una sola. */
export function checkBackup(
  backup: Pick<Backup, 'songs' | 'arrangements' | 'revisions' | 'practice'>,
): Issue[] {
  const issues: Issue[] = []
  const error = (code: string, path: string, message: string) =>
    issues.push({ severity: 'error', code, path, message })
  const warning = (code: string, path: string, message: string) =>
    issues.push({ severity: 'warning', code, path, message })

  const unique = <T extends { id: string }>(items: T[], kind: string) => {
    const map = new Map<string, T>()
    for (const item of items) {
      if (map.has(item.id)) error('id.duplicate', `${kind}:${item.id}`, `ID repetido en ${kind}`)
      map.set(item.id, item)
    }
    return map
  }
  const songs = unique(backup.songs, 'songs')
  const arrangements = unique(backup.arrangements, 'arrangements')
  const revisions = unique(backup.revisions, 'revisions')

  for (const arrangement of backup.arrangements) {
    const path = `arrangements:${arrangement.id}`
    if (!songs.has(arrangement.songId)) {
      error('arrangement.orphan', path, 'El arreglo apunta a una canción que no está en la copia')
    }
    const main = revisions.get(arrangement.mainRevisionId)
    if (!main) {
      error('arrangement.no-main', path, 'La revisión principal no está en la copia')
    } else if (main.arrangementId !== arrangement.id) {
      error('arrangement.wrong-main', path, 'La revisión principal es de otro arreglo')
    }
  }
  for (const song of backup.songs) {
    if (!backup.arrangements.some((arrangement) => arrangement.songId === song.id)) {
      warning('song.no-arrangement', `songs:${song.id}`, `«${song.title}» no tiene ningún arreglo`)
    }
  }
  for (const revision of backup.revisions) {
    const path = `revisions:${revision.id}`
    if (!arrangements.has(revision.arrangementId)) {
      error('revision.orphan', path, 'La revisión apunta a un arreglo que no está en la copia')
    }
    if (revision.parentRevisionId !== null) {
      const parent = revisions.get(revision.parentRevisionId)
      if (!parent) error('revision.no-parent', path, 'La revisión padre no está en la copia')
      else if (parent.arrangementId !== revision.arrangementId) {
        error('revision.wrong-parent', path, 'La revisión padre es de otro arreglo')
      }
    }
    const content = validateContent(revision.content)
    if (hasErrors(content)) {
      error('revision.invalid', path, 'El contenido de la revisión no cumple las reglas musicales')
    }
  }
  const practiced = new Set<string>()
  for (const practice of backup.practice ?? []) {
    const path = `practice:${practice.arrangementId}`
    if (practiced.has(practice.arrangementId)) {
      error('practice.duplicate', path, 'Memoria de práctica repetida para un arreglo')
    }
    practiced.add(practice.arrangementId)
    if (!arrangements.has(practice.arrangementId)) {
      // No estropea nada: se descarta al restaurar.
      warning('practice.orphan', path, 'Memoria de práctica de un arreglo que no está en la copia')
    }
  }
  return issues
}

export type ParsedBackup =
  { ok: true; backup: Backup; warnings: Issue[] } | { ok: false; errors: string[] }

/**
 * Lee el texto de una copia. Rechaza lo que no es una copia de Atril, lo que viene de una
 * versión más nueva y lo que no es coherente: una restauración a medias es peor que ninguna.
 */
export function parseBackup(text: string): ParsedBackup {
  let raw: unknown
  try {
    raw = JSON.parse(text.charCodeAt(0) === 0xfeff ? text.slice(1) : text)
  } catch {
    return { ok: false, errors: ['El archivo no es un JSON válido.'] }
  }
  if (typeof raw !== 'object' || raw === null || (raw as { app?: unknown }).app !== 'atril') {
    return { ok: false, errors: ['El archivo no es una copia de seguridad de Atril.'] }
  }
  const { backupVersion, schemaVersion } = raw as {
    backupVersion?: unknown
    schemaVersion?: unknown
  }
  if (
    (typeof backupVersion === 'number' && backupVersion > BACKUP_VERSION) ||
    (typeof schemaVersion === 'number' && schemaVersion > SCHEMA_VERSION)
  ) {
    return {
      ok: false,
      errors: [
        'La copia es de una versión más nueva de Atril: actualiza la app antes de restaurarla.',
      ],
    }
  }
  const parsed = BackupSchema.safeParse(raw)
  if (!parsed.success) {
    const errors = parsed.error.issues
      .slice(0, 5)
      .map((issue) => `${issue.path.join('.') || 'archivo'}: ${issue.message}`)
    return { ok: false, errors }
  }
  const issues = checkBackup(parsed.data)
  const errors = issues.filter((issue) => issue.severity === 'error')
  if (errors.length > 0)
    return { ok: false, errors: errors.slice(0, 5).map((issue) => issue.message) }
  return { ok: true, backup: parsed.data, warnings: issues }
}

const DAY = 24 * 60 * 60 * 1000

/** Primer recordatorio tras un día con datos sin copiar; después, cada semana si hay cambios. */
export const BACKUP_REMINDER_FIRST_MS = DAY
export const BACKUP_REMINDER_EVERY_MS = 7 * DAY

export interface BackupReminderInput {
  /** Instantes en milisegundos desde 1970. */
  now: number
  lastBackupAt: number | null
  lastChangeAt: number | null
  firstDataAt: number | null
}

/** ¿Toca recordar la copia de seguridad? Solo si hay cambios sin copiar. */
export function backupDue({
  now,
  lastBackupAt,
  lastChangeAt,
  firstDataAt,
}: BackupReminderInput): boolean {
  if (lastChangeAt === null) return false
  if (lastBackupAt !== null && lastChangeAt <= lastBackupAt) return false
  const reference = lastBackupAt ?? firstDataAt ?? lastChangeAt
  const wait = lastBackupAt === null ? BACKUP_REMINDER_FIRST_MS : BACKUP_REMINDER_EVERY_MS
  return now - reference >= wait
}
